class StyleQuizComponent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.startBtn = this.querySelector('[data-quiz-start]');
    this.dialog = this.querySelector('dialog-component');
    this.stepsContainer = this.querySelector('[data-quiz-steps]');
    this.backBtn = this.querySelector('[data-quiz-back]');
    this.progressSegments = Array.from(this.querySelectorAll('[data-quiz-progress-seg]'));
    this.processingEl = this.querySelector('[data-quiz-processing]');
    this.processingMessageEl = this.querySelector('[data-quiz-processing-message]');
    this.leadEl = this.querySelector('[data-quiz-lead]');
    this.leadForm = this.querySelector('#QuizLeadForm');
    this.leadNameInput = this.querySelector('[data-quiz-lead-name]');
    this.leadFirstNameInput = this.querySelector('[data-quiz-lead-first-name]');
    this.leadLastNameInput = this.querySelector('[data-quiz-lead-last-name]');
    this.leadPhoneInput = this.querySelector('[data-quiz-lead-phone]');
    this.leadEmailInput = this.querySelector('[data-quiz-lead-email]');
    this.leadErrorEl = this.querySelector('[data-quiz-lead-error]');
    this.leadSubmitBtn = this.querySelector('[data-quiz-lead-submit]');
    this.resultEl = this.querySelector('[data-quiz-result]');
    this.resultTitleEl = this.querySelector('[data-quiz-result-title]');
    this.resultDescriptionEl = this.querySelector('[data-quiz-result-description]');
    this.compatWrapEl = this.querySelector('[data-quiz-compat-wrap]');
    this.compatValueEl = this.querySelector('[data-quiz-compat-value]');
    this.reasonsEl = this.querySelector('[data-quiz-reasons]');
    this.productsEl = this.querySelector('[data-quiz-products]');
    this.tryOnEl = this.querySelector('[data-quiz-try-on]');
    this.couponEl = this.querySelector('[data-quiz-coupon]');
    this.couponCodeEl = this.querySelector('[data-quiz-coupon-code]');
    this.couponCopyBtn = this.querySelector('[data-quiz-coupon-copy]');

    this.requireEmail = this.dataset.requireEmail === 'true';
    this.processingSeconds = parseFloat(this.dataset.processingSeconds || '4');
    this.compatBase = parseInt(this.dataset.compatBase || '92', 10);

    const configEl = this.querySelector('[data-quiz-config]');
    try {
      this.config = JSON.parse(configEl?.textContent || '{}');
    } catch (e) {
      this.config = { processingMessages: [], reasonsPool: [], couponCodes: [] };
    }

    this.steps = Array.from(this.stepsContainer?.querySelectorAll(':scope > [data-quiz-step]') || []);
    this.stepIndex = -1;
    this.answers = []; // { stepEl, options: [optionEl, ...] }
    this.leadCaptured = false;

    this.startBtn?.addEventListener('click', () => this.start());
    this.backBtn?.addEventListener('click', () => this.goBack());
    this.leadForm?.addEventListener('submit', (event) => this.handleLeadSubmit(event));
    this.couponCopyBtn?.addEventListener('click', () => this.copyCoupon());
    // Máscara brasileira ((DD) 9XXXX-XXXX) enquanto a pessoa digita — só aceita
    // número, e o formato já deixa claro o que falta preencher, reduzindo o
    // risco de erro de digitação no WhatsApp.
    this.leadPhoneInput?.addEventListener('input', () => this.formatPhoneInput());

    this.addEventListener('click', (event) => {
      const optionBtn = event.target.closest('[data-quiz-option]');
      if (optionBtn) this.selectOption(optionBtn);

      const nextBtn = event.target.closest('[data-quiz-next]');
      if (nextBtn) this.advance();
    });

    // O anel pulsante do botão "Começar" só precisa rodar enquanto ele está
    // realmente visível na tela — sem isso, ficaria animando pra sempre em
    // segundo plano assim que o cliente rolasse a página, gastando bateria/CPU
    // à toa numa seção que fica logo no topo da home.
    if (this.startBtn && 'IntersectionObserver' in window) {
      this.startBtnObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.startBtn.classList.toggle('style-quiz__start-btn--paused', !entry.isIntersecting);
          });
        },
        { threshold: 0.01 }
      );
      this.startBtnObserver.observe(this.startBtn);
    }
  }

  start() {
    this.dialog?.showDialog?.();
    // O botão fica atrás do modal enquanto o quiz está aberto — sem motivo
    // pra manter o anel pulsante rodando escondido.
    this.startBtn?.classList.add('style-quiz__start-btn--paused');
    this.dialog?.addEventListener(
      'dialog:close',
      () => this.startBtn?.classList.remove('style-quiz__start-btn--paused'),
      { once: true }
    );
    this.stepIndex = -1;
    this.answers = [];
    this.steps.forEach((el) => (el.hidden = true));
    if (this.resultEl) this.resultEl.hidden = true;
    if (this.leadEl) this.leadEl.hidden = true;
    if (this.processingEl) this.processingEl.hidden = true;
    if (this.stepsContainer) this.stepsContainer.hidden = false;
    this.goToStep(0);
  }

  goToStep(index) {
    if (index >= this.steps.length) {
      this.runProcessing();
      return;
    }
    this.steps.forEach((el, i) => {
      el.hidden = i !== index;
    });
    this.stepIndex = index;
    this.updateProgress();
    if (this.backBtn) this.backBtn.hidden = index === 0;

    const stepEl = this.steps[index];
    if (stepEl?.dataset.inputType === 'multi') {
      this.refreshMultiSelectionState(stepEl);
    }
  }

  updateProgress() {
    if (!this.progressSegments.length) return;
    this.progressSegments.forEach((seg, i) => {
      seg.classList.toggle('style-quiz__progress-seg--done', i <= this.stepIndex);
    });
  }

  selectOption(optionBtn) {
    const stepEl = optionBtn.closest('[data-quiz-step]');
    if (!stepEl) return;
    const inputType = stepEl.dataset.inputType;

    if (inputType === 'multi') {
      const maxSelections = parseInt(stepEl.dataset.maxSelections || '3', 10);
      const alreadySelected = optionBtn.getAttribute('aria-pressed') === 'true';
      const currentCount = stepEl.querySelectorAll('[data-quiz-option][aria-pressed="true"]').length;

      if (!alreadySelected && currentCount >= maxSelections) return;

      optionBtn.setAttribute('aria-pressed', alreadySelected ? 'false' : 'true');
      this.refreshMultiSelectionState(stepEl);
      return;
    }

    // single / image: seleciona e avança direto
    stepEl.querySelectorAll('[data-quiz-option]').forEach((el) => el.setAttribute('aria-pressed', 'false'));
    optionBtn.setAttribute('aria-pressed', 'true');
    this.recordAnswer(stepEl, [optionBtn]);
    this.advance();
  }

  refreshMultiSelectionState(stepEl) {
    const nextBtn = stepEl.querySelector('[data-quiz-next]');
    const selectedCount = stepEl.querySelectorAll('[data-quiz-option][aria-pressed="true"]').length;
    if (nextBtn) nextBtn.hidden = selectedCount === 0;
  }

  advance() {
    const stepEl = this.steps[this.stepIndex];
    if (stepEl?.dataset.inputType === 'multi') {
      const selected = Array.from(stepEl.querySelectorAll('[data-quiz-option][aria-pressed="true"]'));
      if (!selected.length) return;
      this.recordAnswer(stepEl, selected);
    }
    this.goToStep(this.stepIndex + 1);
  }

  goBack() {
    if (this.stepIndex <= 0) return;
    this.answers = this.answers.filter((a) => a.stepEl !== this.steps[this.stepIndex]);
    this.goToStep(this.stepIndex - 1);
  }

  recordAnswer(stepEl, optionEls) {
    this.answers = this.answers.filter((a) => a.stepEl !== stepEl);
    this.answers.push({ stepEl, options: optionEls });
  }

  runProcessing() {
    if (this.stepsContainer) this.stepsContainer.hidden = true;
    if (this.processingEl) this.processingEl.hidden = false;

    const messages = this.config.processingMessages?.length
      ? this.config.processingMessages
      : ['Analisando suas respostas...'];
    let i = 0;
    if (this.processingMessageEl) this.processingMessageEl.textContent = messages[0];
    const stepMs = Math.max(600, (this.processingSeconds * 1000) / messages.length);
    const interval = setInterval(() => {
      i += 1;
      if (i >= messages.length) {
        clearInterval(interval);
        return;
      }
      if (this.processingMessageEl) this.processingMessageEl.textContent = messages[i];
    }, stepMs);

    setTimeout(() => {
      clearInterval(interval);
      if (this.processingEl) this.processingEl.hidden = true;
      if (this.requireEmail && !this.leadCaptured) {
        if (this.leadEl) this.leadEl.hidden = false;
      } else {
        this.revealResult();
      }
    }, this.processingSeconds * 1000);
  }

  // Formata o WhatsApp como (DD) 9XXXX-XXXX enquanto a pessoa digita, aceitando
  // só números — reduz o risco de dado errado por causa de espaço/traço/parêntese
  // digitado do jeito errado.
  formatPhoneInput() {
    if (!this.leadPhoneInput) return;
    const digits = this.leadPhoneInput.value.replace(/\D/g, '').slice(0, 11);
    const ddd = digits.slice(0, 2);
    const rest = digits.slice(2);
    let formatted = '';
    if (digits.length > 0) formatted += `(${ddd}`;
    if (digits.length >= 2) formatted += ') ';
    if (rest.length > 5) {
      formatted += `${rest.slice(0, 5)}-${rest.slice(5)}`;
    } else if (rest.length > 4) {
      formatted += `${rest.slice(0, 4)}-${rest.slice(4)}`;
    } else {
      formatted += rest;
    }
    this.leadPhoneInput.value = formatted;
  }

  // Separa "Nome completo" em first_name/last_name pros campos ocultos que a
  // Shopify espera — a pessoa só vê um campo único, mais simples de preencher.
  splitFullName(fullName) {
    const parts = fullName.trim().split(/\s+/);
    const first = parts.shift() || '';
    const last = parts.join(' ');
    return { first, last };
  }

  handleLeadSubmit(event) {
    const name = (this.leadNameInput?.value || '').trim();
    const phoneDigits = (this.leadPhoneInput?.value || '').replace(/\D/g, '');
    const email = (this.leadEmailInput?.value || '').trim();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const phoneValid = phoneDigits.length === 10 || phoneDigits.length === 11;

    let errorMessage = '';
    if (!name) {
      errorMessage = 'Digite seu nome pra continuar.';
    } else if (!phoneValid) {
      errorMessage = 'Confira o WhatsApp (com DDD) antes de continuar.';
    } else if (!emailValid) {
      errorMessage = 'Digite um e-mail válido pra continuar.';
    }

    if (errorMessage) {
      event.preventDefault();
      if (this.leadErrorEl) {
        this.leadErrorEl.hidden = false;
        this.leadErrorEl.textContent = errorMessage;
      }
      return;
    }

    // Preenche os campos ocultos (nome dividido, telefone em E.164 com +55)
    // logo antes do envio real — assim o formulário visível fica simples pro
    // cliente preencher, mas o dado chega estruturado certo no Admin.
    const { first, last } = this.splitFullName(name);
    if (this.leadFirstNameInput) this.leadFirstNameInput.value = first;
    if (this.leadLastNameInput) this.leadLastNameInput.value = last;
    if (this.leadPhoneInput) this.leadPhoneInput.value = `+55${phoneDigits}`;

    // Válido: NÃO chamamos preventDefault aqui — deixamos o <form> nativo
    // ({% form 'customer' %}, visível na tela) submeter de verdade pro iframe
    // oculto. É essa submissão "de verdade" (clique real, formulário visível)
    // que dá pro script de proteção contra spam da Shopify anexar o token de
    // CAPTCHA; um fetch cru ou um form.submit() via JS não passam por isso.
    if (this.leadErrorEl) this.leadErrorEl.hidden = true;
    this.leadCaptured = true;

    if (this.leadEl) this.leadEl.hidden = true;
    this.revealResult();
  }

  findStyleSelection() {
    for (const answer of this.answers) {
      for (const optionEl of answer.options) {
        if (optionEl.dataset.collection) return optionEl;
      }
    }
    return null;
  }

  findPriceRange() {
    for (const answer of this.answers) {
      for (const optionEl of answer.options) {
        const tag = optionEl.dataset.tag || '';
        if (tag.startsWith('price:')) {
          const raw = tag.replace('price:', '');
          if (raw === 'sem-limite') return null;
          const [min, max] = raw.split('-').map((n) => parseInt(n, 10));
          if (!Number.isNaN(min) && !Number.isNaN(max)) return { min, max };
        }
      }
    }
    return null;
  }

  // Agrupa as tags de produto marcadas nas respostas por passo (ex: formato-redondo,
  // rosto-oval, estilo-minimalista, Acetato) — exceto a de faixa de preço, que tem
  // sua própria leitura em findPriceRange(). Cada passo vira um grupo "OU" (ex: se o
  // cliente marcou formato-redondo e formato-gatinho no mesmo passo de múltipla
  // escolha, basta o produto ter uma das duas); entre passos diferentes, é "E"
  // (ex: precisa bater o formato de armação E o formato de rosto).
  findFilterTagGroups() {
    const groups = [];
    for (const answer of this.answers) {
      const group = answer.options
        .map((optionEl) => (optionEl.dataset.tag || '').trim())
        .filter((tag) => tag && !tag.startsWith('price:'));
      if (group.length) groups.push(group);
    }
    return groups;
  }

  async revealResult() {
    if (this.resultEl) this.resultEl.hidden = false;

    const styleOption = this.findStyleSelection();
    const priceRange = this.findPriceRange();
    const filterTagGroups = this.findFilterTagGroups();

    if (this.resultTitleEl) {
      this.resultTitleEl.textContent = styleOption?.dataset.resultTitle || styleOption?.dataset.label || 'Seu estilo';
    }
    if (this.resultDescriptionEl) {
      const descTemplate = styleOption
        ? this.querySelector(`#${CSS.escape(styleOption.dataset.resultDescId || '')}`)
        : null;
      this.resultDescriptionEl.innerHTML = descTemplate ? descTemplate.innerHTML : '';
    }

    // Compatibilidade: começa no teto configurado e desconta um pouco pra cada
    // critério (estilo escolhido, faixa de preço) que não pôde ser aplicado.
    let compat = this.compatBase;
    if (!styleOption) compat -= 15;
    if (this.answers.length && !priceRange) compat -= 3;
    compat = Math.max(70, Math.min(this.compatBase, compat));
    if (this.compatWrapEl) this.compatWrapEl.hidden = false;
    if (this.compatValueEl) this.compatValueEl.textContent = `${compat}%`;

    if (this.reasonsEl) {
      const reasons = (this.config.reasonsPool || []).slice(0, 4);
      this.reasonsEl.innerHTML = reasons.map((r) => `<li>${r}</li>`).join('');
    }

    await this.renderProducts(styleOption, priceRange, filterTagGroups);

    if (this.couponEl && this.config.couponCodes?.length) {
      const code = this.config.couponCodes[Math.floor(Math.random() * this.config.couponCodes.length)];
      if (this.couponCodeEl) this.couponCodeEl.textContent = code;
      this.couponEl.hidden = false;
      this.burstConfetti(this.couponEl);
    }
  }

  burstConfetti(container) {
    // Confete leve em CSS puro (sem canvas/libs): algumas divs coloridas que
    // nascem no centro e se espalham em direções/rotação aleatórias, reforçando
    // a sensação de recompensa ao desbloquear o cupom.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const colors = ['#111111', '#ffb703', '#ef476f', '#06d6a0', '#118ab2'];
    for (let i = 0; i < 14; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'style-quiz__confetti-piece';
      const angle = Math.random() * Math.PI * 2;
      const distance = 60 + Math.random() * 60;
      piece.style.setProperty('--confetti-x', `${Math.cos(angle) * distance}px`);
      piece.style.setProperty('--confetti-y', `${Math.sin(angle) * distance - 40}px`);
      piece.style.setProperty('--confetti-rot', `${Math.round(Math.random() * 360)}deg`);
      piece.style.background = colors[i % colors.length];
      piece.style.animationDelay = `${Math.random() * 0.1}s`;
      container.appendChild(piece);
      piece.addEventListener('animationend', () => piece.remove());
    }
  }

  async renderProducts(styleOption, priceRange, filterTagGroups) {
    if (!this.productsEl) return;
    const handle = styleOption?.dataset.collection;
    if (!handle) {
      this.productsEl.innerHTML = '<p>Não foi possível carregar recomendações agora.</p>';
      return;
    }

    try {
      const res = await fetch(`/collections/${handle}/products.json?limit=24`);
      const data = await res.json();
      let products = data.products || [];

      // Refinamento por tags das respostas (formato, rosto, estilo, material):
      // filtra dentro da coleção de estilo já escolhida. Se cruzar tudo e não
      // sobrar nada, volta pra lista completa da coleção — igual já acontecia
      // com o filtro de preço, pra nunca mostrar a tela vazia.
      if (filterTagGroups && filterTagGroups.length) {
        const groups = filterTagGroups.map((group) => group.map((t) => t.toLowerCase()));
        const refined = products.filter((p) => {
          const productTags = (p.tags || []).map((t) => t.toLowerCase());
          return groups.every((group) => group.some((t) => productTags.includes(t)));
        });
        if (refined.length) products = refined;
      }

      if (priceRange) {
        const filtered = products.filter((p) => {
          const price = parseFloat(p.variants?.[0]?.price || '0');
          return price >= priceRange.min && price <= priceRange.max;
        });
        if (filtered.length) products = filtered;
      }

      const top = products.slice(0, 4);
      this.productsEl.innerHTML = top
        .map((p) => {
          const image = p.images?.[0]?.src || '';
          const price = parseFloat(p.variants?.[0]?.price || '0');
          const formattedPrice = price
            ? price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            : '';
          return `
            <a class="style-quiz__product-card" href="/products/${p.handle}">
              ${image ? `<img src="${image}&width=300" alt="${p.title}">` : ''}
              <span>${p.title}</span>
              <span>${formattedPrice}</span>
            </a>
          `;
        })
        .join('');

      if (this.tryOnEl && top.length) {
        this.tryOnEl.href = `/products/${top[0].handle}`;
        this.tryOnEl.hidden = false;
      }
    } catch (e) {
      this.productsEl.innerHTML = '<p>Não foi possível carregar recomendações agora.</p>';
    }
  }

  copyCoupon() {
    const code = this.couponCodeEl?.textContent;
    if (!code) return;
    navigator.clipboard?.writeText(code).then(() => {
      const original = this.couponCopyBtn.textContent;
      this.couponCopyBtn.textContent = 'Copiado!';
      setTimeout(() => {
        this.couponCopyBtn.textContent = original;
      }, 1500);
    });
  }
}

if (!customElements.get('style-quiz-component')) {
  customElements.define('style-quiz-component', StyleQuizComponent);
}
